/**
 * Данные для таймлайна плана лечения.
 *
 * План приходит «плоско»: направления (groups) → услуги (items) → приёмы
 * (appointments, по одному на каждый номер приёма). Для таймлайна нужен
 * обратный разрез — по этапам (номерам приёмов), с датами из
 * `result.admission` и признаком выполнения.
 *
 * Здесь только чистая логика без React, чтобы экран оставался компактным.
 */
import { localizedSuffix } from "../../../i18n/localizedService";

const BAD_DATES = ["0000-00-00 00:00", "0000-00-00 00:00:00"];

export const isValidDateValue = (value) =>
    Boolean(value) &&
    !BAD_DATES.includes(value) &&
    !Number.isNaN(new Date(value).getTime());

export const appointmentNumber = (appointment) =>
    parseInt(appointment?.appointment_number?.value, 10);

export const hasAdmission = (appointment) => {
    const id = appointment?.id_admission?.value;
    return id !== undefined && String(id) !== "0" && String(id) !== "";
};

export const toNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

/** Название услуги с приоритетом языка интерфейса. */
export const pickLocalizedValue = (record, key, language) => {
    const suffix = localizedSuffix(language);
    const primary = record?.[`${key}${suffix}`]?.value;
    if (primary != null && String(primary).trim() !== "") return primary;

    const secondary = record?.[suffix ? key : `${key}_en`]?.value;
    if (secondary != null && String(secondary).trim() !== "") return secondary;

    return "";
};

/** Дата в виде dd.mm (для компактных подписей этапов). */
export const formatDayMonth = (value) => {
    if (!isValidDateValue(value)) return "";
    const [datePart] = String(value).split(" ");
    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) return "";
    return `${day}.${month}`;
};

/** Диапазон дат этапа: «12.08 – 15.08» либо одна дата. */
export const formatRange = (start, end) => {
    const from = formatDayMonth(start);
    const to = formatDayMonth(end);
    if (!from && !to) return "";
    if (!from) return to;
    if (!to || to === from) return from;
    return `${from} – ${to}`;
};

// Все приёмы (admissions) этапа: по услугам этапа собираем id_admission.
const collectAdmissions = (entries, admissions) => {
    const ids = new Set();
    entries.forEach(({ appointment }) => {
        const id = appointment?.id_admission?.value;
        if (id && String(id) !== "0" && !ids.has(String(id))) {
            ids.add(String(id));
        }
    });
    return Array.from(ids)
        .map((id) => admissions?.[id])
        .filter(Boolean);
};

/**
 * Строит список этапов (приёмов) плана.
 *
 * @param {object}  params
 * @param {Array}   params.groups            — направления плана
 * @param {object}  params.typeMap           — «номер приёма → подпись этапа»
 * @param {object}  params.admissions        — result.admission (даты, давление)
 * @param {string}  params.language          — язык интерфейса
 * @param {Function} params.stageFallback    — подпись этапа по умолчанию
 * @param {Function} params.groupLabel       — подпись направления
 * @returns {Array} этапы по возрастанию номера
 */
export const buildTimelineStages = ({
    groups = [],
    typeMap = {},
    admissions = {},
    language = "ru",
    stageFallback = (number) => `#${number}`,
    groupLabel = () => "",
}) => {
    // 1. Максимальный номер приёма — по лимитам групп и самим назначениям.
    let maxNumber = 0;
    groups.forEach((group) => {
        const limit = Number(group?.count_appointment?.value) || 0;
        if (limit > maxNumber) maxNumber = limit;
        (group?.items || []).forEach((item) => {
            (item?.appointments || []).forEach((apt) => {
                const number = appointmentNumber(apt);
                if (Number.isFinite(number) && number > maxNumber) {
                    maxNumber = number;
                }
            });
        });
    });

    const numbers = Array.from({ length: maxNumber }, (_, index) => index + 1);

    // 2. По каждому номеру — услуги всех направлений, попавшие в этот приём.
    const stages = numbers.map((number) => {
        const entries = [];
        groups.forEach((group) => {
            (group?.items || []).forEach((item) => {
                (item?.appointments || []).forEach((appointment) => {
                    if (appointmentNumber(appointment) !== number) return;
                    entries.push({ group, item, appointment });
                });
            });
        });

        const stageAdmissions = collectAdmissions(entries, admissions);

        // Даты: фактическая дата приёма, если её нет — плановая.
        const dates = stageAdmissions
            .map((info) => info?.date?.value || info?.date_plan?.value)
            .filter(isValidDateValue)
            .sort((a, b) => new Date(a) - new Date(b));

        const actualDates = stageAdmissions
            .map((info) => info?.date?.value)
            .filter(isValidDateValue)
            .sort((a, b) => new Date(a) - new Date(b));

        const planDates = stageAdmissions
            .map((info) => info?.date_plan?.value)
            .filter(isValidDateValue)
            .sort((a, b) => new Date(a) - new Date(b));

        const services = entries.map(({ group, item, appointment }) => {
            const admissionInfo = appointment?.id_admission?.value
                ? admissions?.[appointment.id_admission.value]
                : null;
            return {
                key: `${item?.id?.value}-${number}`,
                id: item?.id?.value,
                name: pickLocalizedValue(item, "name", language) || "—",
                composition: item?.composition?.value || "",
                price: toNumber(item?.amount?.value ?? item?.amount_base?.value),
                done: hasAdmission(appointment),
                date:
                    admissionInfo?.date?.value ||
                    admissionInfo?.date_plan?.value ||
                    "",
                comment: String(appointment?.comment?.value ?? "").trim(),
                groupLabel: groupLabel(group),
            };
        });

        const total = services.length;
        const done = services.filter((service) => service.done).length;

        const pressure =
            stageAdmissions
                .map((info) => String(info?.pressure?.value ?? "").trim())
                .find(Boolean) || "";

        const comment =
            services
                .map((service) => service.comment)
                .find(Boolean) ||
            stageAdmissions
                .map((info) => String(info?.comment?.value ?? "").trim())
                .find(Boolean) ||
            "";

        return {
            number,
            title: typeMap[number] || stageFallback(number),
            services,
            total,
            done,
            start: dates[0] || "",
            end: dates[dates.length - 1] || "",
            planDate: planDates[0] || "",
            actualDate: actualDates[actualDates.length - 1] || "",
            pressure,
            comment,
            sum: services.reduce((acc, service) => acc + service.price, 0),
            state: "future",
        };
    });

    // 3. Текущий этап — первый, где выполнены не все услуги. Этапы без услуг
    //    не считаем «текущими», иначе пустой столбец перетягивал бы фокус.
    const firstIncomplete = stages.find(
        (stage) => stage.total > 0 && stage.done < stage.total,
    );
    const currentNumber = firstIncomplete
        ? firstIncomplete.number
        : stages.length
          ? stages[stages.length - 1].number
          : 0;

    stages.forEach((stage) => {
        if (stage.total > 0 && stage.done === stage.total) {
            stage.state = "done";
            return;
        }
        if (!stage.total) {
            stage.state = stage.number < currentNumber ? "done" : "future";
            return;
        }
        if (stage.number < currentNumber) {
            stage.state = "done";
            return;
        }
        stage.state = stage.number === currentNumber ? "current" : "future";
    });

    return stages;
};
